var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { SurveyCreator } from "survey-creator-react";
import { DEFAULT_CREATOR_OPTIONS, FORM_LOCALE, } from "../constants/forms.constants";
import { FORMS_EXCLUDED_TOOLBOX_ITEMS, SURVEY_ALLOWED_COMPONENTS, } from "./survey-allowed-components";
import { configureGlobalCreatorPropertyGrid } from "./survey-hidden-properties-grid-infos";
import { slugifyTitle } from "./helpers";
import { configureCreatorHtmlSanitization, sanitizeSurveySchemaHtml, } from "./survey-html-sanitization";
export var defineAutoCompleteOptions = function (Serializer, autocompleteValues) {
    var _a;
    var prop = Serializer.findProperty("text", "autocomplete");
    prop.choices.length = 0;
    (_a = prop.choices).push.apply(_a, autocompleteValues);
};
export var changePreFormInfo = function (key, value, setPreFormInfo) {
    setPreFormInfo(function (prev) {
        var _a;
        return (__assign(__assign({}, prev), (_a = {}, _a[key] = value, _a)));
    });
};
export var formHasPages = function (creator) {
    return "pages" in JSON.parse(creator.text);
};
export var getFormValidationError = function (preFormInfo, creator) {
    var _a, _b;
    if (!((_a = preFormInfo.title) === null || _a === void 0 ? void 0 : _a.trim())) {
        return "O título do formulário é obrigatório !";
    }
    if (!slugifyTitle((_b = preFormInfo.slug) !== null && _b !== void 0 ? _b : "")) {
        return "O slug do formulário é obrigatório !";
    }
    if (!formHasPages(creator)) {
        return "O formulário deve ter pelo menos um campo !";
    }
    return null;
};
export var getApiErrorMessage = function (err, fallback) {
    var _a, _b, _c, _d;
    var e = err;
    return (_d = (_c = (_b = (_a = e === null || e === void 0 ? void 0 : e.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) !== null && _c !== void 0 ? _c : e === null || e === void 0 ? void 0 : e.message) !== null && _d !== void 0 ? _d : fallback;
};
var PANEL_TYPE = "panel";
var NON_VALUE_PRODUCING_TYPES = new Set(["html", "image"]);
var COMMENT_FIELD_SUFFIX = "-Comment";
var flattenElements = function (elements) {
    if (elements === void 0) { elements = []; }
    return elements.flatMap(function (element) {
        var _a;
        return element.type === PANEL_TYPE && ((_a = element.elements) === null || _a === void 0 ? void 0 : _a.length)
            ? flattenElements(element.elements)
            : [element];
    });
};
var usesCommentKey = function (element, surveyStoreOthersAsComment) {
    var _a;
    if (element.showCommentArea)
        return true;
    if (!element.showOtherItem)
        return false;
    return (_a = element.storeOthersAsComment) !== null && _a !== void 0 ? _a : surveyStoreOthersAsComment;
};
export var extractFormFieldNames = function (creator) {
    var _a, _b;
    try {
        var schema = JSON.parse(creator.text);
        var storeOthersAsComment_1 = (_a = schema.storeOthersAsComment) !== null && _a !== void 0 ? _a : true;
        return ((_b = schema.pages) !== null && _b !== void 0 ? _b : []).flatMap(function (page) {
            return flattenElements(page.elements).flatMap(function (element) {
                var _a, _b;
                if (NON_VALUE_PRODUCING_TYPES.has((_a = element.type) !== null && _a !== void 0 ? _a : ""))
                    return [];
                var valueKey = (_b = element.valueName) !== null && _b !== void 0 ? _b : element.name;
                if (!valueKey)
                    return [];
                return usesCommentKey(element, storeOthersAsComment_1)
                    ? [valueKey, "".concat(valueKey).concat(COMMENT_FIELD_SUFFIX)]
                    : [valueKey];
            });
        });
    }
    catch (_c) {
        return [];
    }
};
export var buildCallbackPayloadPreview = function (formName, serviceSlug, fieldNames, includeUserAuth, submittedAt) { return (__assign({ formName: formName || "<nome-do-formulario>", serviceSlug: serviceSlug || "<slug-do-servico>", submittedAt: submittedAt, answers: fieldNames.reduce(function (acc, name) {
        acc[name] = "<...>";
        return acc;
    }, {}) }, (includeUserAuth ? { userId: "<identificador-do-usuario>" } : {}))); };
export var buildBaseFormPayload = function (preFormInfo, creator) {
    var schema = JSON.parse(creator.text);
    return {
        title: preFormInfo.title,
        slug: slugifyTitle(preFormInfo.slug),
        description: preFormInfo.description,
        schema: sanitizeSurveySchemaHtml(schema),
        callbackURL: preFormInfo.callbackURL,
        callbackIncludeUserAuth: preFormInfo.callbackURL
            ? !!preFormInfo.callbackIncludeUserAuth
            : false,
        authRequired: !!(preFormInfo === null || preFormInfo === void 0 ? void 0 : preFormInfo.authRequired),
    };
};
export var focusFormInputAfterValidationError = function (ref) {
    setTimeout(function () {
        if (ref.current) {
            ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
            setTimeout(function () { return ref.current.focus(); }, 100);
        }
    }, 100);
};
var createBaseSurveyCreator = function (extra) {
    var surveyCreator = new SurveyCreator(__assign(__assign({}, DEFAULT_CREATOR_OPTIONS), extra));
    surveyCreator.locale = FORM_LOCALE;
    configureCreatorHtmlSanitization(surveyCreator);
    configureGlobalCreatorPropertyGrid(surveyCreator);
    return surveyCreator;
};
export var createFormsSurveyCreator = function (extra) {
    var surveyCreator = createBaseSurveyCreator(extra);
    FORMS_EXCLUDED_TOOLBOX_ITEMS.forEach(function (itemName) {
        surveyCreator.toolbox.removeItem(itemName);
    });
    return surveyCreator;
};
export var createNpsSurveyCreator = function (extra) {
    return createBaseSurveyCreator(__assign({ questionTypes: SURVEY_ALLOWED_COMPONENTS }, extra));
};
var JSON_EDITOR_TAB_ID = "editor";
var DESIGNER_TAB_ID = "designer";
export var forceSyncJsonEditorTab = function (creator) {
    if (creator.activeTab === JSON_EDITOR_TAB_ID) {
        creator.activeTab = DESIGNER_TAB_ID;
    }
};
